import type { ReactNode } from "react";
import { IconButton, Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box, { type BoxProps } from "../Box";
import { CardIconHeader } from "../Cards/CardIconHeader";
import { FlatCard } from "../Cards/FlatCard";

type FrameProps = {
	children: ReactNode;
};

const Frame = ({ children }: FrameProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	// Paper's Surface warns when overflow is hidden on the card itself, so the
	// clipping lives on an inner wrapper with the card's border radius.
	const borderRadius = isMobile ? 0 : theme.roundness * 3;
	return (
		<FlatCard style={isMobile ? { borderRadius: 0 } : undefined}>
			<Box overflow="hidden" borderRadius={borderRadius}>
				{children}
			</Box>
		</FlatCard>
	);
};

type HeaderProps = {
	title: string;
	subtitle?: string;
	icon?: IconType;
	trailing?: ReactNode;
	onPressMenu?: () => void;
};

const Header = ({
	title,
	subtitle,
	icon,
	trailing,
	onPressMenu,
}: HeaderProps) => {
	const theme = useAppTheme();

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			gap={8}
			width="100%"
			borderBottomWidth={1}
			borderColor={theme.colors.surfaceVariant}
			padding={16}
		>
			<Box flex={1} minWidth={0}>
				{icon ? (
					<CardIconHeader icon={icon} title={title} subtitle={subtitle} />
				) : (
					<Box gap={4}>
						<Text
							variant="titleMedium"
							style={{ color: theme.colors.onSurface }}
						>
							{title}
						</Text>
						{subtitle ? (
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{subtitle}
							</Text>
						) : null}
					</Box>
				)}
			</Box>
			{trailing || onPressMenu ? (
				<Box flexDirection="row" alignItems="center" flexShrink={0}>
					{trailing}
					{onPressMenu ? (
						<IconButton icon="dots-vertical" size={24} onPress={onPressMenu} />
					) : null}
				</Box>
			) : null}
		</Box>
	);
};

type BodyProps = {
	children: ReactNode;
} & BoxProps;

const Body = ({ children, ...props }: BodyProps) => {
	return <Box {...props}>{children}</Box>;
};

type FooterProps = {
	children?: ReactNode;
	render?: () => ReactNode;
} & BoxProps;

const Footer = ({ children, render, ...props }: FooterProps) => {
	const content = render ? render() : children;
	if (!content) {
		return null;
	}

	return (
		<Box
			width="100%"
			flexDirection="row"
			justifyContent="flex-end"
			alignItems="center"
			gap={8}
			{...props}
		>
			{content}
		</Box>
	);
};

export const SessionReportCard = {
	Frame,
	Header,
	Body,
	Footer,
};
